import { Component, Input, ViewChild, ElementRef, SimpleChanges, OnDestroy, OnChanges, ChangeDetectorRef, Output, EventEmitter, Renderer2, ViewEncapsulation } from '@angular/core';
import { WorkItemChat } from 'common/workitems/models/work-item-chat';
import { MESSAGE_UPDATE_BUFFER_DURATION } from 'components/work-item-lane-list/work-item-lane/work-item-widget/chat-widget/chat-box/chat-box.component';
import { WorkItemTypes } from 'common/workitems/constants/workitem-types';
import { ChatMessageGroupModel } from 'shared/components/chat-messages/chat-message-group.model';
import { VisitorInfoModel } from 'shared/models/visitor-info/visitor-info.model';
import { ChatItem } from 'common/workitems/interfaces/chat-item.interface';
import { Subscription } from 'rxjs';
import { ThemeServiceHelper } from 'common/theme/service.helper';
import { MessageGroupService } from 'shared/services/message-group/message-group.service';
import { UserContextService } from 'common/user-context/user-context.service';
import { bufferTime } from 'rxjs/operators';
import { merge } from 'lodash';
import { bcEnums } from 'common/common/constants/boldchat-enums';
import { JoinChatComponent } from 'shared/components/join-chat/join-chat.component';
import { MessageBusEvents } from 'common/common/constants/message-bus-events';
import { RxEmitter } from 'common/rxjs/rx-emitter';
import { BcVisitorInfoBuilderService } from 'components/work-item-lane-list/work-item-lane/work-item-widget/shared/services/visitor-info-builder.service';
import { ChatsService } from 'common/chats/chats.service';
import { VisitsService } from 'common/visits/visits.service';
import { ChatMessagesStoreFactory } from 'common/chat-messages-store-factory/chat-messages-store-factory.service';

@Component({
  selector: 'app-chat-viewer',
  templateUrl: './chat-viewer.component.html',
  styleUrls: ['./chat-viewer.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class ChatViewerComponent implements OnDestroy, OnChanges {
  @Input()
  chatId: string;

  @Input()
  tabName: string;

  @Input()
  draggable: boolean = false;

  @Input()
  dragBoundaryElementSelector: string;

  @Input()
  showActivityIndicator: boolean = false;

  @Input()
  hasFullScreenRc: boolean = false;

  @Output()
  closeAction: EventEmitter<void> = new EventEmitter();

  @ViewChild('scrollPane')
  scrollPane: ElementRef;

  @ViewChild(JoinChatComponent)
  joinChatComponent: JoinChatComponent;

  public workItemType = WorkItemTypes.CHAT;
  public messages: ChatMessageGroupModel[] = [];
  public visitor: VisitorInfoModel;
  public chatThemeClass: string;
  public workItem: ChatItem;
  public messagesLoading: boolean;
  public workItemCreated: string | number;
  public cannedMessageRequest;
  public isCannedMessagePanelOpen: boolean;

  private _isAttached: boolean;
  private _hasNewMessage: boolean = false;
  private _chatInsertObservable: Subscription;
  private _chatUpdateObservable: Subscription;
  private _messagesSubscription: Subscription;
  private currentUrlSubscription: Subscription;
  private _cannedMessageReceivedSubscription: Subscription;
  private _cannedMessageClosedSubscription: Subscription;

  constructor(private _chatMessagesStore: ChatMessagesStoreFactory,
              private _bcVisitorInfoBuilderService: BcVisitorInfoBuilderService,
              private _bcVisits: VisitsService,
              private _messageGroupService: MessageGroupService,
              private _userContextService: UserContextService,
              public chats: ChatsService,
              private _changeDetector: ChangeDetectorRef,
              private _element: ElementRef,
              private _renderer: Renderer2,
              private _rxEmitter: RxEmitter) {
  }

  ngOnDestroy(): void {
    this._clearSubscriptions();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.chatId) {
      if (changes.chatId.currentValue !== changes.chatId.previousValue) {
        // Reload only if it's a different chat
        this._clearSubscriptions();
        this._initChat();
      }
    }

    if (changes.showActivityIndicator) {
      this._hasNewMessage = false;
    }
  }

  isConversationActive(): boolean {
    return this.workItem && !this.workItem.Ended && !this.workItem.Closed && !!this.workItem.OperatorID;
  }

  isChatInProgress(): boolean {
    return this.isConversationActive() && !!this.workItem.Answered;
  }

  isChatAnswerable(): boolean {
    return this.isConversationActive() && this.workItem && !this.workItem.LastAssignedAnswered && this.workItem.OperatorID === this._userContextService.userContext.id;
  }

  answerChat(): Promise<void> {
    if (this.workItem && this.isChatAnswerable()) {
      return this.chats.answerChat(this.workItem).then(() => {
        this._forceScrollToBottom();
      });
    }
  }

  closeButtonClicked(): void {
    this.closeAction.emit();
  }

  dragFinishedHandler() {
    this._updateCannedMessagePanelOpenPosition();
  }

  toggleCannedMessagePanel() {
    this.isCannedMessagePanelOpen = !this.isCannedMessagePanelOpen;
    if (this.isCannedMessagePanelOpen) {
      this._updateCannedMessagePanelOpenPosition();
    }
  }

  getUnansweredMessageCount() {
    return this._hasNewMessage ? 1 : 0;
  }

  get isEditorVisible(): boolean {
    return this.joinChatComponent && this.joinChatComponent.isEditorVisible();
  }

  _updateCannedMessagePanelOpenPosition() {
    const rect = this._element.nativeElement.querySelector('.app-chat-viewer').getBoundingClientRect();
    if (rect.right > window.innerWidth - 360) {
      this._renderer.addClass(this._element.nativeElement, 'right-most-item-on-screen');
    } else {
      this._renderer.removeClass(this._element.nativeElement, 'right-most-item-on-screen');
    }
  }

  private _initChat(): void {
    this._observeChat();
    this._loadMessages();
    this._getChatClass();

    this._cannedMessageReceivedSubscription = this._rxEmitter.subscribe(`${MessageBusEvents.SendCannedMessageToEditor}${this.chatId}-chat-viewer`, (cannedMessage) => {
      if (this.joinChatComponent) {
        this.joinChatComponent.addCannedMessage(cannedMessage);
      }
      this.isCannedMessagePanelOpen = false;
    });
    this._cannedMessageClosedSubscription = this._rxEmitter.subscribe(`${MessageBusEvents.CloseCannedMessagePanel}${this.chatId}-chat-viewer`, () => {
      this.isCannedMessagePanelOpen = false;
    });
  }

  private _observeChat(): void {
    const chatObservable = this.chats.observeChatByChatId(this.chatId);
    this._chatInsertObservable = chatObservable.inserted().subscribe(chat => {
      this.workItem = new WorkItemChat(chat);
      this._updateCreatedDateFormat();
      this._buildVisitorInfo();
      this.cannedMessageRequest = {
        type: bcEnums.cannedMessageTypes.CHAT,
        id: chat.ChatID,
        chatWindowDefId: chat.ChatWindowDefID,
        language: chat.LanguageCode
      };
      this._changeDetector.detectChanges();
    });
    this._chatUpdateObservable = chatObservable.updated().subscribe(chat => {
      this.workItem = new WorkItemChat(chat);
      this._updateCreatedDateFormat();
      this._changeDetector.detectChanges();
    });
  }

  private _updateCreatedDateFormat(): void {
    if (this.workItem.Created) {
      this.workItemCreated = isNaN(Number(this.workItem.Created)) ? this.workItem.Created : Number(this.workItem.Created);
    }
  }

  private _loadMessages(): void {
    this.messages = [];
    this.messagesLoading = true;
    this._messagesSubscription = this._chatMessagesStore.create(this.chatId).pipe(
      bufferTime(MESSAGE_UPDATE_BUFFER_DURATION)
    ).subscribe(messagesBuffer => {
      if (messagesBuffer.length) {
        if (this.showActivityIndicator) {
          this._hasNewMessage = true;
        }
        this.setMessages(messagesBuffer);
        this.scrollToBottomIfMessageReceived(messagesBuffer);
      }
      if (this.messagesLoading) {
        this.messagesLoading = false;
        this._hasNewMessage = false;
        this._changeDetector.detectChanges();
        this._forceScrollToBottom();
      }
    });
  }

  private _clearSubscriptions(): void {
    if (this._chatInsertObservable) {
      this._chatInsertObservable.unsubscribe();
      this._chatInsertObservable = null;
    }
    if (this._chatUpdateObservable) {
      this._chatUpdateObservable.unsubscribe();
      this._chatUpdateObservable = null;
    }
    if (this._messagesSubscription) {
      this._messagesSubscription.unsubscribe();
      this._messagesSubscription = null;
    }
    if (this.currentUrlSubscription) {
      this.currentUrlSubscription.unsubscribe();
      this.currentUrlSubscription = null;
    }
    if (this._cannedMessageReceivedSubscription) {
      this._cannedMessageReceivedSubscription.unsubscribe();
      this._cannedMessageReceivedSubscription = null;
    }
    if (this._cannedMessageClosedSubscription) {
      this._cannedMessageClosedSubscription.unsubscribe();
      this._cannedMessageClosedSubscription = null;
    }
  }

  private _buildVisitorInfo(): void {
    this.visitor = null;
    Promise.all([
      this._bcVisitorInfoBuilderService.getVisitorInfoModelFromChat(this.workItem),
      this._bcVisitorInfoBuilderService.getFolderName(this.workItem),
      this._bcVisitorInfoBuilderService.getDepartmentName(this.workItem),
      this._bcVisitorInfoBuilderService.getWebsite(this.workItem)
    ]).then((result) => {
      this.visitor = merge({}, this.visitor, result[0]);
      this.visitor.fields.folderName.value = result[1];
      this.visitor.fields.departmentName.value = result[2];
      this.visitor.fields.website.value = result[3];
      this.createCurrentUrlObservable();
      this._changeDetector.detectChanges();
    });
  }

  private _getChatClass(): void {
    this.chatThemeClass = ThemeServiceHelper.getCssClass(this.chatId);
  }

  setMessages(messagesBuffer): void {
    if (!messagesBuffer.length) {
      return;
    }
    this.setIsAttachedToBottom();
    this.messages = this._messageGroupService.addNewMessagesToGroups(this.messages, messagesBuffer[messagesBuffer.length - 1], this.workItemType);
    this._changeDetector.detectChanges();
  }

  setIsAttachedToBottom(): void {
    if (!this.scrollPane) {
      this._isAttached = true;
    } else {
      const scrollEl = this.scrollPane.nativeElement;
      this._isAttached = scrollEl.scrollTop + scrollEl.clientHeight + 1 >= scrollEl.scrollHeight;
    }
  }

  scrollToBottomIfMessageReceived(messagesBuffer): void {
    if (messagesBuffer.length) {
      this.scrollToBottom();
    }
  }

  scrollToBottom(): void {
    if (this.scrollPane && this._isAttached) {
      this.scrollPane.nativeElement.scrollTop = this.scrollPane.nativeElement.scrollHeight;
    }
  }

  private _forceScrollToBottom(): void {
    this._isAttached = true;
    this.scrollToBottom();
  }

  createCurrentUrlObservable(): void {
    if (!this.currentUrlSubscription && this.workItem.VisitID && this.workItem.VisitorID) {
      this.currentUrlSubscription = this._bcVisits.observeVisitInfoByVisitId(this.workItem.VisitID, this.workItem.VisitorID).subscribe(visit => {
        this.visitor.fields.currentUrl.value = visit.data.CurrentUrl;
        this._changeDetector.detectChanges();
      });
    }
  }

  onJoinStateChange(): void {
    this._forceScrollToBottom();
  }

  onChatMessagesRendered() {
    this._changeDetector.detectChanges();
    this._forceScrollToBottom();
  }
}
